import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for Google Places Autocomplete
 * @param {Object} options - Configuration options
 * @param {string} options.value - Current input value
 * @param {Function} options.onPlaceSelect - Callback when a place is selected
 * @param {Object} options.restrictions - Place type restrictions (e.g., { country: 'in' })
 * @param {Array} options.types - Place types (e.g., ['geocode', 'establishment'])
 * @returns {Object} - { inputRef, suggestions, isOpen, setIsOpen }
 */
export const useGooglePlacesAutocomplete = ({
  value,
  onPlaceSelect,
  restrictions = { country: 'in' }, // Default to India
  types = ['geocode', 'establishment']
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  // Load Google Maps script
  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsGoogleLoaded(true);
      return;
    }

    // Get API key from environment variable
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    // Debug: Log all environment variables that start with REACT_APP_ (for debugging)
    const reactAppVars = Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'));
    console.log('Available REACT_APP_ environment variables:', reactAppVars);
    
    // Debug: Log API key status (first 10 chars only for security)
    if (apiKey) {
      console.log('✅ Google Maps API Key loaded:', apiKey.substring(0, 10) + '...');
    } else {
      console.error('❌ Google Maps API key not found!');
      console.error('Please ensure:');
      console.error('1. The .env file exists in the project root (same folder as package.json)');
      console.error('2. The .env file contains: REACT_APP_GOOGLE_MAPS_API_KEY=your_key_here');
      console.error('3. You have RESTARTED the React development server after creating/editing .env');
      console.error('4. The server was started with "npm start" from the project root');
    }
    
    // If no API key is provided, don't try to load Google Maps
    if (!apiKey || apiKey === 'YOUR_API_KEY') {
      console.info('Google Maps API key not configured. Location autocomplete will use fallback city list.');
      return;
    }

    // Check if script tag already exists in HTML
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script already in HTML, just wait for it to load
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsGoogleLoaded(true);
          clearInterval(checkGoogle);
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    }

    // Load Google Maps script dynamically
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Check if Google Maps loaded successfully
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log('✅ Google Maps API loaded successfully');
        setIsGoogleLoaded(true);
      } else {
        console.warn('⚠️ Google Maps script loaded but Places API not available');
        console.warn('Please ensure Places API is enabled in Google Cloud Console');
      }
      
      // Listen for API errors
      if (window.google && window.google.maps) {
        window.google.maps.event.addListenerOnce(window, 'error', (error) => {
          if (error && error.message) {
            console.error('❌ Google Maps API Error:', error.message);
            if (error.message.includes('ApiTargetBlockedMapError') || 
                error.message.includes('not authorized')) {
              console.error('');
              console.error('🔧 SOLUTION: Enable Places API in Google Cloud Console');
              console.error('1. Go to: https://console.cloud.google.com/');
              console.error('2. Navigate to: APIs & Services > Library');
              console.error('3. Search for "Places API" and click Enable');
              console.error('4. Ensure billing is enabled (required for Places API)');
              console.error('5. Check API key restrictions allow Places API');
              console.error('');
            }
          }
        });
      }
    };
    script.onerror = () => {
      console.error('❌ Google Maps API failed to load. Please check:');
      console.error('1. Your API key in .env file');
      console.error('2. That Places API is enabled in Google Cloud Console');
      console.error('3. That your API key has proper restrictions');
      console.error('4. That billing is enabled for your Google Cloud project');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Sync input value when value prop changes (important for edit mode)
  useEffect(() => {
    if (inputRef.current && value !== undefined && value !== null) {
      // Only update if the value is different to avoid unnecessary updates
      if (inputRef.current.value !== value) {
        inputRef.current.value = value;
      }
    }
  }, [value]);

  // Initialize Autocomplete when Google Maps is loaded and input is available
  useEffect(() => {
    if (!isGoogleLoaded) return;

    // Function to initialize autocomplete
    const initAutocomplete = () => {
      if (!inputRef.current) {
        return false; // Input not available yet
      }

      // Skip if already initialized (check for existing autocomplete)
      if (inputRef.current.getAttribute('data-gm-autocomplete')) {
        return true; // Already initialized
      }

      // Clean up previous autocomplete instance if it exists
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      // Initialize Autocomplete
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: types,
            componentRestrictions: restrictions,
            fields: ['formatted_address', 'geometry', 'name', 'place_id']
          }
        );

        autocompleteRef.current = autocomplete;
        inputRef.current.setAttribute('data-gm-autocomplete', 'true');
        console.log('✅ Google Places Autocomplete initialized for input');

        // Listen for place selection
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place && place.formatted_address) {
            const fullAddress = place.formatted_address;
            console.log('📍 Place selected:', fullAddress);
            // Ensure the input field has the full address before calling onPlaceSelect
            if (inputRef.current) {
              inputRef.current.value = fullAddress;
            }
            // Call onPlaceSelect to update formData
            onPlaceSelect(fullAddress, place);
            setIsOpen(false);
          }
        });
        return true;
      } catch (error) {
        console.error('❌ Error initializing Google Places Autocomplete:', error);
        console.error('This usually means:');
        console.error('1. Places API is not enabled in Google Cloud Console');
        console.error('2. API key restrictions are blocking the request');
        console.error('3. Billing is not enabled for your Google Cloud project');
        return false;
      }
    };

    // Try to initialize immediately
    if (initAutocomplete()) {
      return () => {
        if (autocompleteRef.current && window.google && window.google.maps) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
      };
    }

    // If input not available, poll for it (useful for modals)
    let attempts = 0;
    const maxAttempts = 20; // Try for 2 seconds (20 * 100ms)
    const pollInterval = setInterval(() => {
      attempts++;
      if (initAutocomplete() || attempts >= maxAttempts) {
        clearInterval(pollInterval);
      }
    }, 100);

    return () => {
      clearInterval(pollInterval);
      if (autocompleteRef.current && window.google && window.google.maps) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isGoogleLoaded, onPlaceSelect, restrictions, types]);

  return {
    inputRef,
    isOpen,
    setIsOpen,
    isGoogleLoaded
  };
};

